(() => {
  const CARD_SELECTOR = '[data-main-product]';
  const VARIANT_CHANGED_EVENT = 'abtest:variant-changed';
  const RESOLVED_EVENT = 'abtest:resolved';
  const GROUP_LABELS = {
    control: '99.99 + Dock',
    'test-with-dock': '89.99 + Dock',
    'watch-only': '89.99 only',
    'test-watch-only': '89.99 only',
    'safewatch-kids-v2': 'Safewatch Kids + Dock'
  };
  const HSW5_TRIGGERS = new Set(['control', 'test-with-dock', 'watch-only', 'test-watch-only']);
  const activeTriggers = new Set(
    Array.isArray(window.ABTEST_VARIANTS) ? window.ABTEST_VARIANTS.map(normalize).filter(Boolean) : []
  );
  const resolvedExperiments = new Set();

  function normalize(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function parseJson(element, fallback) {
    if (!element?.textContent) return fallback;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[A/B Test] Invalid JSON configuration:', error);
      return fallback;
    }
  }

  function uniqueVariantIds(values) {
    return [...new Set(values
      .map((value) => String(value).trim())
      .filter((value) => /^\d+$/.test(value) && Number(value) > 0))];
  }

  function parseAddOnVariantIds(value) {
    if (!value) return [];
    if (Array.isArray(value)) return uniqueVariantIds(value);

    const serializedValue = String(value).trim();
    if (!serializedValue) return [];

    try {
      const parsedValue = JSON.parse(serializedValue);
      if (Array.isArray(parsedValue)) return uniqueVariantIds(parsedValue);
    } catch (error) {
      // Comma-separated data attributes are supported for Liquid convenience.
    }

    return uniqueVariantIds(serializedValue.split(','));
  }

  function groupLabel(trigger) {
    const normalizedTrigger = normalize(trigger);
    if (!normalizedTrigger) return '';
    return GROUP_LABELS[normalizedTrigger] || String(trigger).trim();
  }

  function isHsw5Trigger(trigger) {
    return HSW5_TRIGGERS.has(normalize(trigger));
  }

  function experimentKey(trigger) {
    const normalizedTrigger = normalize(trigger);
    return isHsw5Trigger(normalizedTrigger) ? 'hsw5' : normalizedTrigger;
  }

  function rememberVariant(testVariant) {
    const activeTrigger = normalize(testVariant);
    if (isHsw5Trigger(activeTrigger)) {
      HSW5_TRIGGERS.forEach((trigger) => activeTriggers.delete(trigger));
    }
    activeTriggers.add(activeTrigger);
    window.ABTEST_VARIANTS = Array.from(activeTriggers);
    window.ABTEST_VARIANT = testVariant;
  }

  function getVariant(experiment) {
    if (normalize(experiment) === 'hsw5') {
      return Array.from(activeTriggers).find((trigger) => HSW5_TRIGGERS.has(trigger));
    }
    return window.ABTEST_VARIANT;
  }

  function updateFormGroup(form, trigger) {
    let input = form.querySelector('[data-abtest-group-property]');
    const label = groupLabel(trigger);

    if (!input && label) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_abtest-group]';
      input.setAttribute('data-abtest-group-property', '');
      form.append(input);
    }

    if (!input) return;
    input.value = label;
    input.disabled = !label;
  }

  function updateFormAddOns(form, addOnVariantId) {
    let dataElement = form.querySelector('[data-product-add-ons]');

    if (!dataElement) {
      dataElement = document.createElement('script');
      dataElement.type = 'application/json';
      dataElement.setAttribute('data-product-add-ons', '');
      dataElement.textContent = '[]';
      form.prepend(dataElement);
    }

    if (!dataElement.dataset.abtestBaseAddOns) {
      dataElement.dataset.abtestBaseAddOns = JSON.stringify(
        uniqueVariantIds(parseJson(dataElement, []))
      );
    }

    const baseIds = parseJson({ textContent: dataElement.dataset.abtestBaseAddOns }, []);
    const ids = baseIds.concat(parseAddOnVariantIds(addOnVariantId));
    dataElement.textContent = JSON.stringify(uniqueVariantIds(ids));
  }

  function cardMatchesTrigger(card, activeTrigger) {
    if (!activeTrigger) return false;
    return normalize(card.dataset.trigger).split(/[\s,]+/).includes(activeTrigger);
  }

  function setCardVisibility(card, isVisible) {
    card.toggleAttribute('hidden', !isVisible);
    card.toggleAttribute('inert', !isVisible);
    card.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  function applyCards(testVariant) {
    const activeTrigger = normalize(testVariant);
    const cards = Array.from(document.querySelectorAll(CARD_SELECTOR));
    const focusedCard = document.activeElement?.closest?.(CARD_SELECTOR);
    const matchingTriggerCards = cards.filter((card) => (
      cardMatchesTrigger(card, activeTrigger)
      || (
        activeTrigger === 'control'
        && card.dataset.abtestExperiment === 'hsw5'
        && !card.hasAttribute('data-trigger')
      )
    ));
    const matchedMainProducts = new Set(
      matchingTriggerCards.map((card) => card.dataset.mainProduct).filter(Boolean)
    );

    if (!matchedMainProducts.size) return;

    cards.forEach((card) => {
      if (!matchedMainProducts.has(card.dataset.mainProduct)) return;
      setCardVisibility(card, false);
    });

    matchingTriggerCards.forEach((card) => {
      setCardVisibility(card, true);
    });

    cards.forEach((card) => {
      if (!matchedMainProducts.has(card.dataset.mainProduct)) return;
      const isVisible = !card.hasAttribute('hidden');
      card.querySelectorAll('[data-product-form]').forEach((form) => {
        updateFormAddOns(form, isVisible ? card.dataset.addOnVariant : '');
        updateFormGroup(form, isVisible && card.hasAttribute('data-trigger') ? testVariant : '');
      });
    });

    if (focusedCard?.hidden) {
      const replacement = cards.find((card) => (
        card.dataset.mainProduct === focusedCard.dataset.mainProduct && !card.hidden
      ));
      const focusSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusTarget = replacement?.matches?.(focusSelector)
        ? replacement
        : replacement?.querySelector(focusSelector);
      focusTarget?.focus();
    }
  }

  function pageConfig() {
    return parseJson(document.querySelector('[data-abtest-page-config]'), null);
  }

  function applyPage(testVariant) {
    const config = pageConfig();
    if (!config?.isPdp) return false;
    const activeTrigger = normalize(testVariant);
    const activeTest = config?.variants?.find((item) => normalize(item.trigger) === activeTrigger);
    if (!activeTest) return false;

    const templateRedirect = String(activeTest?.templateRedirect || '').trim();
    if (templateRedirect) {
      const target = new URL(window.location.href);
      if (target.searchParams.get('view') !== templateRedirect) {
        target.searchParams.set('view', templateRedirect);
        window.location.replace(target.toString());
        return true;
      }
    }

    document.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.closest(CARD_SELECTOR)) return;
      updateFormAddOns(form, activeTest?.addOnVariantId || '');
      updateFormGroup(form, testVariant);
    });
    return false;
  }

  function updateHsw5Forms(testVariant) {
    if (!isHsw5Trigger(testVariant)) return;

    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const productRoot = form.closest('[data-product-handle]');
      if (productRoot?.dataset.productHandle !== 'health-smartwatch-5') return;
      updateFormGroup(form, testVariant);
    });
  }

  function releasePendingButtons() {
    document.querySelectorAll('[data-hsw5-ab-pending]').forEach((button) => {
      button.removeAttribute('data-hsw5-ab-pending');
      button.removeAttribute('aria-busy');
      button.disabled = button.dataset.variantAvailable !== 'true';
    });
  }

  function dispatchResolved(testVariant, fallback = false) {
    const experiment = fallback ? 'hsw5' : experimentKey(testVariant);
    if (experiment) resolvedExperiments.add(experiment);
    window.ABTEST_RESOLVED = true;
    window.dispatchEvent(new CustomEvent(RESOLVED_EVENT, {
      detail: { testVariant, trigger: testVariant, experiment, fallback }
    }));
    if (experiment === 'hsw5') releasePendingButtons();
  }

  function apply(testVariant) {
    if (applyPage(testVariant)) return false;
    applyCards(testVariant);
    updateHsw5Forms(testVariant);
    window.dispatchEvent(new CustomEvent(VARIANT_CHANGED_EVENT, {
      detail: {
        testVariant,
        trigger: testVariant,
        experiment: experimentKey(testVariant),
        normalizedTrigger: normalize(testVariant),
        groupLabel: groupLabel(testVariant)
      }
    }));
    return true;
  }

  function setVariant(testVariant) {
    if (!normalize(testVariant)) {
      console.warn('[A/B Test] Ignored empty setVariant() trigger:', testVariant);
      return;
    }

    rememberVariant(testVariant);
    if (apply(testVariant)) dispatchResolved(testVariant, false);
  }

  window.ABTEST_RESOLVED = window.ABTEST_RESOLVED === true;
  window.setVariant = setVariant;
  window.setVariant.abtestHandler = true;

  // Deprecated compatibility shim. Some cached Intelligems content may still
  // call the removed HSW5-specific API; ignore it without running an experiment.
  window.HSW5_AB = window.HSW5_AB || {};
  window.HSW5_AB.setVariant = () => undefined;

  window.ABTestHandler = {
    apply,
    getGroupLabel: groupLabel,
    getVariant,
    hasVariant: (trigger) => activeTriggers.has(normalize(trigger)),
    isResolved: (experiment) => resolvedExperiments.has(normalize(experiment))
  };

  document.addEventListener('shopify:section:load', () => {
    activeTriggers.forEach((trigger) => apply(trigger));
  });

  function initialize() {
    activeTriggers.forEach((trigger) => {
      if (apply(trigger)) dispatchResolved(trigger, false);
    });

    window.setTimeout(() => {
      if (!resolvedExperiments.has('hsw5')) dispatchResolved(undefined, true);
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
